// Compare up to three projects side by side. Choosing two or more shows the
// table at once (no extra click); the link in the address bar can be shared.
// Each row reads across on a desktop and stacks on a phone, so there is no
// sideways scrolling anywhere.
import { useEffect, useState } from 'react';
import { useSite } from '../lib/site-context.jsx';
import { formatPrice, monthYear, projectHref } from '../lib/format.js';

const KEYS = ['p1', 'p2', 'p3'];

const list = (v) => (Array.isArray(v) ? v : []);

const ROWS = [
  { label: 'Developer', get: (p) => p.developer_name || '—' },
  { label: 'Location', get: (p) => [p.city, p.sector].filter(Boolean).join(', ') || '—' },
  { label: 'Status', get: (p) => p.status || '—' },
  { label: 'Type', get: (p) => p.project_type || '—' },
  { label: 'Price range', get: (p) => <span className="price-value">{formatPrice(p)}</span>, highlight: true },
  { label: 'Unit types', get: (p) => <Chips items={list(p.unit_types)} /> },
  { label: 'Possession', get: (p) => (p.possession_date ? monthYear(p.possession_date) : '—') },
  { label: 'RERA No.', get: (p) => p.rera_number || '—' },
  { label: 'Amenities', get: (p) => <Chips items={list(p.amenities)} /> },
  { label: 'Locality', get: (p) => p.locality_notes || '—' },
];

function Chips({ items }) {
  if (!items.length) return '—';
  return <ul className="chip-list">{items.map((a) => <li className="chip" key={a}>{a}</li>)}</ul>;
}

export default function Compare({ data }) {
  const { t } = useSite();
  const all = data.projects || [];
  const [sel, setSel] = useState(() => KEYS.map((k) => data.selected?.[k] || ''));
  const [copied, setCopied] = useState(false);
  const chosen = sel.map((id) => all.find((p) => p.id === id)).filter(Boolean);
  const ready = chosen.length >= 2;

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    KEYS.forEach((k) => sp.delete(k));
    sel.filter(Boolean).forEach((id, i) => sp.set(KEYS[i], id));
    window.history.replaceState({}, '', sp.toString() ? `?${sp}` : window.location.pathname);
  }, [sel]);

  const share = () => navigator.clipboard?.writeText(window.location.href).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });

  return (
    <>
      <section className="section page-head">
        <div className="wrap">
          <h1>{t('compare_title', 'Compare projects')}</h1>
          <p className="page-lede">{t('compare_sub', 'Select up to 3 projects and compare specs, pricing and amenities side by side.')}</p>
        </div>
      </section>
      <section className="section section-flush">
        <div className="wrap">
          <div className="selector-row">
            {sel.map((value, i) => (
              <div className="field" key={KEYS[i]}>
                <label htmlFor={`sel-${i + 1}`}>{i === 2 ? 'Third project (optional)' : i === 0 ? 'First project' : 'Second project'}</label>
                <select id={`sel-${i + 1}`} value={value} onChange={(e) => setSel((s) => s.map((x, j) => (j === i ? e.target.value : x)))}>
                  <option value="">{i === 2 ? '— Add a third (optional) —' : '— Select project —'}</option>
                  {all.map((p) => <option key={p.id} value={p.id} disabled={sel.includes(p.id) && sel[i] !== p.id}>{p.name} — {p.city}</option>)}
                </select>
              </div>
            ))}
          </div>
          <p className="selector-hint" id="sel-hint" aria-live="polite">{ready ? `${chosen.length} projects selected.` : 'Choose at least 2 projects to compare.'}</p>

          {ready && (
            <>
              <div className="toolbar">
                <button type="button" className="btn btn-outline btn-sm" id="compare-print" onClick={() => window.print()}>Print / save PDF</button>
                <button type="button" className="btn btn-outline btn-sm" id="share-btn" onClick={share}>{copied ? 'Link copied' : 'Copy share link'}</button>
              </div>
              <div className={`compare cols-${chosen.length}`} id="compare-output" role="table" aria-label="Project comparison">
                <div className="compare-row compare-head" role="row">
                  <div className="compare-label" role="columnheader"><span className="sr-only">Project</span></div>
                  {chosen.map((p) => (
                    <div className="compare-cell" role="columnheader" key={p.id}>
                      <a className="compare-name" href={projectHref(p)}>{p.name}</a>
                      {p.tagline && <div className="muted">{p.tagline}</div>}
                    </div>
                  ))}
                </div>
                {ROWS.map((row) => (
                  <div className={`compare-row${row.highlight ? ' row-highlight' : ''}`} role="row" key={row.label}>
                    <div className="compare-label" role="rowheader">{row.label}</div>
                    {chosen.map((p) => <div className="compare-cell" role="cell" data-label={p.name} key={p.id}>{row.get(p)}</div>)}
                  </div>
                ))}
                <div className="compare-row" role="row">
                  <div className="compare-label" role="rowheader">Enquire</div>
                  {chosen.map((p) => (
                    <div className="compare-cell compare-actions" role="cell" data-label={p.name} key={p.id}>
                      <a className="btn btn-rust btn-sm" href={`/contact?project=${encodeURIComponent(p.id)}`}>Enquire now</a>
                      <a className="btn btn-outline btn-sm" href={`/visit?project=${encodeURIComponent(p.id)}`}>Book site visit</a>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
